import React, { type JSX } from 'react'
import styled, { css } from 'styled-components'
import type { ColorKey } from '../../tokens/colors'
import type { TypographyVariant } from '../../tokens/typography'

interface TypographyProps {
  as?: keyof JSX.IntrinsicElements
  variant: TypographyVariant
  color?: ColorKey | string
  align?: 'left' | 'center' | 'right' | 'justify'
  ellipsis?: boolean
  children?: React.ReactNode | React.ReactNode[] | string
  className?: string
}

const StyledTypography = styled.span<{
  $variant: TypographyVariant
  $color?: ColorKey | string
  $align?: 'left' | 'center' | 'right' | 'justify'
  $ellipsis?: boolean
}>`
  ${({ $variant, theme }) => {
    const styles = theme.typography.variants[$variant]
    return css`
      font-size: ${styles.fontSize};
      line-height: ${styles.lineHeight};
      font-weight: ${styles.fontWeight};
      letter-spacing: ${styles.letterSpacing};
    `
  }}

  color: ${({ $color, theme }) => {
    if (!$color) return 'inherit'
    // theme.colors 객체에서 색상을 찾고, 없으면 원본 값 사용
    return theme.colors[$color as ColorKey] || $color
  }};

  ${({ $align }) =>
    $align &&
    css`
      text-align: ${$align};
    `}

  ${({ $ellipsis }) =>
    $ellipsis &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
`

export function Typography({
  as = 'span',
  variant,
  color,
  align,
  ellipsis,
  children,
  className,
}: TypographyProps) {
  return (
    <StyledTypography
      as={as}
      $variant={variant}
      $color={color}
      $align={align}
      $ellipsis={ellipsis}
      className={className}
    >
      {children}
    </StyledTypography>
  )
}
